import { Controller } from "@hotwired/stimulus"
import { parseDate } from "../../helpers/date_helpers"
import { RangeFieldsModel } from "../../configs/range_fields_model"

// Connects to data-controller="configs--form"
export default class extends Controller {
  static targets = [
    'mode', 'period', 'comparison', 'range', 'followCount', 'grouping'
  ]

  static values = {
    mode: { type: String, default: 'preset' },
    originalPeriod: { type: String, default: 't12m' },
    presetData: { type: Object, default: {} },
    followCount: { type: Number, default: 0 }
  }

  static classes = ['custom', 'preset', 'comparison']

  connect() {
    app.cfgForm = this
    this.setModeAppearance()
  }

  modeValueChanged() {
    this.modeTarget.value = this.modeValue
  }

  change(event) {
    if (this.period == 'custom' && this.modeValue != 'custom') {
      this.changeModeToCustom()
    } else if (this.period != 'custom' && this.modeValue != 'preset') {
      this.changeModeToPreset({ restorePeriod: false })
    }

    if (this.period != 'custom') this.originalPeriodValue = this.period

    this.applyPresetRangeData(this.period)
  }

  applyPresetRangeData(period) {
    const rangeData = this.presetDataValue?.map?.[period]?.[this.comparison] || this.customRangeData
    const suggestedUnit = this.presetDataValue?.map?.[period]?.unit

    if (!rangeData) return

    this.rangeTargets.forEach((t) => {
      const entry = rangeData[t.dataset.position]

      if (entry) {
        t.dataset["configs-RangeDateMinValue"] = entry.min
        t.dataset["configs-RangeDateMaxValue"] = entry.max
      }
    })

    this.selectGroupingRadioFor(suggestedUnit)
  }

  selectGroupingRadioFor(unit) {
    if (!unit) return

    const radio = this.groupingTargets.find((target) => target.value === unit)
    if (radio) radio.checked = true
  }

  changeMode(event) {
    if (this.modeValue == 'preset') {
      this.changeModeToCustom()
    } else {
      this.changeModeToPreset()
    }
  }

  changeModeToCustom() {
    this.modeValue = 'custom'
    this.setPresetToCustom()
    this.dispatch("modeChange", { detail: { mode: 'custom' } })
    this.setModeAppearance()
  }

  changeModeToPreset({ restorePeriod = true } = {}) {
    this.modeValue = 'preset'
    if (restorePeriod) this.setPresetToOriginal()
    this.dispatch("modeChange", { detail: { mode: 'preset' } })
    this.setModeAppearance()
  }

  setModeAppearance() {
    if (this.modeValue == "custom") {
      this.element.classList.remove(...this.presetClasses)
      this.element.classList.add(...this.customClasses)
    } else {
      this.element.classList.remove(...this.customClasses)
      this.element.classList.add(...this.presetClasses)
    }
  }

  setPresetToCustom() {
    this.periodTarget.value = 'custom'
  }

  setPresetToOriginal() {
    const period = this.originalPeriodValue == 'custom' ? 't12m' : this.originalPeriodValue
    this.periodTarget.value = period
    this.applyPresetRangeData(period)
  }

  get period() {
    return this.periodTarget.value
  }

  get comparison() {
    return this.comparisonTargets.find((target) => target.checked)?.value
  }

  get customRangeData() {
    return new RangeFieldsModel({
      mode: this.modeValue,
      period: this.period,
      comparison: this.comparison,
      presetData: this.presetDataValue,
      leaderRange: this.period === 'custom' ? [
        parseDate(this.leaderRange.dataset["configs-RangeDateMinValue"]),
        parseDate(this.leaderRange.dataset["configs-RangeDateMaxValue"])
      ] : undefined
    }).selectedPeriodRanges
  }

  get leaderRange() {
    return this.rangeTargets.find(target => target.dataset.role === 'leader')
  }

  get followerRanges() {
    return this.rangeTargets.filter(target => target.dataset.role !== 'leader')
  }
}
